/**
 * OP Readiness — Mismatch browser tab (WP7.5).
 * Field summary for a chosen audit run + per-product expected-vs-actual drill, with severity and
 * field filters and a client-side CSV export for business review.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  fetchOPReadinessAuditRuns,
  fetchOPReadinessMismatches,
  isUnavailable,
} from '@/lib/op-readiness';
import { SeverityBadge, StatusBadge, ValueCell, UnavailableNotice, formatDateTime } from './shared';

function toCsv(rows: Record<string, unknown>[]): string {
  if (!rows.length) return '';
  const cols = Object.keys(rows[0]);
  const escape = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  return [cols.join(','), ...rows.map((r) => cols.map((c) => escape(r[c])).join(','))].join('\n');
}

export function MismatchBrowser() {
  const [runId, setRunId] = useState<string | null>(null);
  const [severity, setSeverity] = useState('');
  const [field, setField] = useState('');
  const [page, setPage] = useState(1);

  const runsQuery = useQuery({
    queryKey: ['op-readiness', 'audit-runs'],
    queryFn: fetchOPReadinessAuditRuns,
  });
  const runs = useMemo(() => {
    const d = runsQuery.data;
    if (!d || isUnavailable(d)) return [];
    return d.runs.filter((r) => r.HasItems === 1);
  }, [runsQuery.data]);

  const effectiveRunId = runId ?? runs[0]?.AuditRunId ?? null;

  const mismatchQuery = useQuery({
    queryKey: ['op-readiness', 'mismatches', effectiveRunId, severity, field, page],
    queryFn: () =>
      fetchOPReadinessMismatches(effectiveRunId!, {
        severity: severity || undefined,
        field: field || undefined,
        page,
        pageSize: 100,
      }),
    enabled: !!effectiveRunId,
  });

  if (runsQuery.data && isUnavailable(runsQuery.data)) {
    return <UnavailableNotice reason={runsQuery.data.reason} />;
  }
  if (runsQuery.isLoading) return <Skeleton className="h-64 w-full rounded-lg" />;
  if (!effectiveRunId) {
    return <p className="text-sm text-muted-foreground">No audit runs with per-product items yet — run the Stage 5 audit first.</p>;
  }

  const data = mismatchQuery.data && !isUnavailable(mismatchQuery.data) ? mismatchQuery.data : null;
  const fieldOptions = ['', ...(data?.fieldSummary.map((f) => f.FieldPath) ?? [])];
  const pages = Math.max(1, Math.ceil((data?.total ?? 0) / 100));

  const exportCsv = () => {
    if (!data) return;
    const csv = toCsv(data.items as unknown as Record<string, unknown>[]);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `op-mismatches-${effectiveRunId.slice(0, 8)}-p${page}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <select
          className="h-9 rounded-md border bg-background px-2 text-sm"
          value={effectiveRunId}
          onChange={(e) => { setRunId(e.target.value); setPage(1); }}
        >
          {runs.map((r) => (
            <option key={r.AuditRunId} value={r.AuditRunId}>
              {formatDateTime(r.StartedAt)} — {r.TotalRecords} sampled, parity {r.ParityPct ?? '—'}%
            </option>
          ))}
        </select>
        <select
          className="h-9 rounded-md border bg-background px-2 text-sm"
          value={severity}
          onChange={(e) => { setSeverity(e.target.value); setPage(1); }}
        >
          <option value="">Any severity</option>
          <option value="critical">critical</option>
          <option value="warning">warning</option>
          <option value="info">info</option>
        </select>
        <select
          className="h-9 rounded-md border bg-background px-2 text-sm"
          value={field}
          onChange={(e) => { setField(e.target.value); setPage(1); }}
        >
          {fieldOptions.map((f) => <option key={f} value={f}>{f || 'Any field'}</option>)}
        </select>
        <Button size="sm" variant="outline" onClick={exportCsv} disabled={!data?.items.length}>
          Export CSV
        </Button>
        <span className="ml-auto text-sm text-muted-foreground">
          {data?.total ?? 0} mismatch entries · page {page}/{pages}
        </span>
      </div>

      {data && (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b bg-muted/40 text-xs uppercase text-muted-foreground">
                <th className="px-3 py-2">Field</th><th className="px-3 py-2">Severity</th>
                <th className="px-3 py-2">EAN</th><th className="px-3 py-2">Audit status</th>
                <th className="px-3 py-2">Our payload</th><th className="px-3 py-2">Live OP</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((m, i) => (
                <tr key={`${m.OPProductPayloadId}-${m.field}-${i}`} className="border-b last:border-0">
                  <td className="px-3 py-2 font-mono text-xs">{m.field}</td>
                  <td className="px-3 py-2"><SeverityBadge severity={m.severity} /></td>
                  <td className="px-3 py-2 font-mono text-xs">{m.EAN ?? '—'}</td>
                  <td className="px-3 py-2"><StatusBadge status={m.AuditStatus} /></td>
                  <td className="px-3 py-2"><ValueCell value={m.expected} /></td>
                  <td className="px-3 py-2"><ValueCell value={m.actual} /></td>
                </tr>
              ))}
              {data.items.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-sm text-muted-foreground">No mismatches for these filters. 🎉</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {mismatchQuery.isLoading && <Skeleton className="h-40 w-full rounded-lg" />}

      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
        <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
      </div>
    </div>
  );
}
